import { ArrowRight, Check, Clock3, RotateCcw } from 'lucide-react';
import { useId, useState } from 'react';

import { Button } from '@/shared/ui/button';

import { fieldNextStep } from '../field-next-step';
import { fieldProgressJourney } from '../field-progress-journey';

import type { FieldDestination } from '../field-overview';
import type {
  FieldActorId,
  FieldProgressStage,
  FieldProgressState,
  FieldSnapshot,
} from '@mio/shared';
import type { CSSProperties } from 'react';

import './field-progress-path.css';

interface FieldProgressPathProps {
  snapshot: FieldSnapshot;
  unreadByActor: Partial<Record<FieldActorId, number>>;
  onNavigate: (destination: FieldDestination) => void;
  onShowDetails: () => void;
  onShowStageDetails: (stage: FieldProgressStage) => void;
}

const states: Record<FieldProgressState, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  waiting: 'Waiting for a response',
  verified: 'Complete',
  needs_recheck: 'Needs another check',
};

const workLabels: Record<FieldProgressStage, string> = {
  agreed: 'Open brief',
  designed: 'Open design',
  built: 'Open code',
  verified: 'Open tests',
  controls: 'Open checks',
  shipped: 'Open delivery',
};

const positions = [
  { x: 70, y: 146, mobileX: 26 },
  { x: 242, y: 117, mobileX: 40 },
  { x: 414, y: 131, mobileX: 32 },
  { x: 586, y: 76, mobileX: 40 },
  { x: 758, y: 91, mobileX: 32 },
  { x: 930, y: 52, mobileX: 26 },
] as const;

const desktopSegments = [
  'M70 146 C138 146 174 117 242 117',
  'M242 117 C310 117 346 131 414 131',
  'M414 131 C482 131 518 76 586 76',
  'M586 76 C654 76 690 91 758 91',
  'M758 91 C826 91 862 52 930 52',
];

const mobileSegments = positions.slice(0, -1).map((position, index) => {
  const y = 26 + index * 54;
  const nextX = (positions[index + 1] ?? position).mobileX;
  return `M${position.mobileX} ${y} C${position.mobileX} ${y + 27} ${nextX} ${y + 27} ${nextX} ${y + 54}`;
});

function MilestoneMark({ state }: { state: FieldProgressState }) {
  if (state === 'verified') return <Check size={17} strokeWidth={2.5} aria-hidden="true" />;
  if (state === 'waiting') return <Clock3 size={16} aria-hidden="true" />;
  if (state === 'needs_recheck') return <RotateCcw size={16} aria-hidden="true" />;
  return <span className="field-progress-path-dot" />;
}

export function FieldProgressPath({
  snapshot,
  unreadByActor,
  onNavigate,
  onShowDetails,
  onShowStageDetails,
}: FieldProgressPathProps) {
  const journey = fieldProgressJourney(snapshot);
  const [selectedStage, setSelectedStage] = useState<FieldProgressStage | null>(null);
  const selected =
    journey.milestones.find((milestone) => milestone.stage === selectedStage) ??
    journey.milestones.find((milestone) => milestone.stage === journey.currentStage) ??
    journey.milestones[0];
  const nextStep = fieldNextStep(snapshot, unreadByActor);
  const headingId = useId();
  const selectionId = useId();
  if (!selected) return null;

  return (
    <div className="field-progress-path-container">
      <section className="field-progress-path" aria-labelledby={headingId}>
        <header className="field-progress-path-header">
          <div>
            <p className="field-progress-path-eyebrow">Your progress</p>
            <h2 id={headingId}>{journey.title}</h2>
            <p className="field-progress-path-description">{journey.description}</p>
          </div>
          <button className="field-progress-path-text-button" type="button" onClick={onShowDetails}>
            View details <ArrowRight size={14} aria-hidden="true" />
          </button>
        </header>

        <div className="field-progress-path-overview">
          <p className="field-progress-path-count">
            <strong>{journey.completedCount}</strong> of {journey.milestones.length} milestones
            complete
          </p>
          <div className="field-progress-path-route">
            {(['desktop', 'mobile'] as const).map((layout) => (
              <svg
                key={layout}
                className={`field-progress-path-vector field-progress-path-vector-${layout}`}
                viewBox={layout === 'desktop' ? '0 0 1000 220' : '0 0 64 324'}
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                {(layout === 'desktop' ? desktopSegments : mobileSegments).map((segment, index) => (
                  <path
                    key={segment}
                    d={segment}
                    data-complete={
                      journey.milestones[index]?.state === 'verified' &&
                      journey.milestones[index + 1]?.state === 'verified'
                    }
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>
            ))}
            <ol className="field-progress-path-stops" aria-label="Project milestones">
              {journey.milestones.map((milestone, index) => {
                const position = positions[index] ?? positions[0];
                const isCurrent = milestone.stage === journey.currentStage;
                return (
                  <li
                    key={milestone.stage}
                    style={
                      {
                        '--stop-x': `${position.x / 10}%`,
                        '--stop-y': `${(position.y / 220) * 100}%`,
                        '--stop-mobile-x': `${position.mobileX}px`,
                        '--stop-mobile-y': `${26 + index * 54}px`,
                      } as CSSProperties
                    }
                  >
                    <button
                      type="button"
                      className="field-progress-path-stop"
                      data-state={milestone.state}
                      data-current={isCurrent}
                      aria-current={isCurrent ? 'step' : undefined}
                      aria-pressed={selected.stage === milestone.stage}
                      aria-controls={selectionId}
                      aria-label={`${milestone.label}: ${states[milestone.state]}. ${milestone.verified} of ${milestone.total} parts confirmed.`}
                      onClick={() => setSelectedStage(milestone.stage)}
                      onDoubleClick={() => onNavigate(milestone.destination)}
                    >
                      <span className="field-progress-path-mark">
                        {milestone.verified > 0 && milestone.verified < milestone.total ? (
                          <svg
                            className="field-progress-path-partial"
                            viewBox="0 0 36 36"
                            aria-hidden="true"
                          >
                            <circle
                              cx="18"
                              cy="18"
                              r="17"
                              pathLength="100"
                              strokeDasharray={`${(milestone.verified / milestone.total) * 100} 100`}
                            />
                          </svg>
                        ) : null}
                        <MilestoneMark state={milestone.state} />
                      </span>
                      <span className="field-progress-path-stop-copy">
                        <span className="field-progress-path-stop-label">{milestone.label}</span>
                        <span className="field-progress-path-stop-status">
                          {milestone.state === 'waiting' || milestone.state === 'needs_recheck'
                            ? states[milestone.state]
                            : milestone.state === 'verified'
                              ? 'Complete'
                              : isCurrent
                                ? 'Up next'
                                : milestone.state === 'in_progress'
                                  ? 'In progress'
                                  : '\u00a0'}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
          <p className="field-progress-path-hint">
            Select a milestone to explore. Double-click to open its work.
          </p>
        </div>

        <section
          className="field-progress-path-selection"
          id={selectionId}
          aria-label={`${selected.label} milestone`}
        >
          <div className="field-progress-path-selection-copy" aria-live="polite" aria-atomic="true">
            <div className="field-progress-path-selection-heading">
              <h3>{selected.label}</h3>
              <span>
                {selected.verified} of {selected.total} parts confirmed
              </span>
            </div>
            <p>{selected.summary}</p>
          </div>
          <div className="field-progress-path-selection-actions">
            <Button size="sm" variant="secondary" onClick={() => onNavigate(selected.destination)}>
              {selected.destination.section === 'assessment'
                ? 'Open final review'
                : workLabels[selected.stage]}
              <ArrowRight size={14} aria-hidden="true" />
            </Button>
            <button
              type="button"
              className="field-progress-path-text-button"
              onClick={() => onShowStageDetails(selected.stage)}
            >
              View evidence
            </button>
          </div>
        </section>

        <footer className="field-progress-path-next">
          <div>
            <span className="field-progress-path-eyebrow">Next step</span>
            <p>{nextStep.title}</p>
          </div>
          <button
            className="field-progress-path-text-button"
            type="button"
            onClick={() => onNavigate(nextStep.destination)}
          >
            {nextStep.label} <ArrowRight size={14} aria-hidden="true" />
          </button>
        </footer>
      </section>
    </div>
  );
}
